import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import {
	AppAutocompleteComponent,
	AppBreadcrumbComponent,
	AppLogoComponent,
	BtnToggleComponent,
	CommentairesFormComponent,
	CommentairesHistoryComponent,
	CommentairesModalComponent,
	ConfirmationEditionModalComponent,
	ConfirmationModalComponent,
	ContentBlockComponent,
	DataTableComponentV2,
	DatagridActionsDropdownComponent,
	DatagridPagingComponent,
	DatagridRowEmptyComponent,
	DatagridTableComponent,
	DocumentBasicUploadComponent,
	DocumentUploadBaseComponent,
	FormControlFeedbackComponent,
	GenerationStatementModalComponent,
	InputGroupAddDropdownComponent,
	LabelHelpComponent,
	LoaderButtonSpinnerComponent,
	LoaderSpinnerComponent,
	PageHeaderComponent,
	TiersFormComponent,
	TiersPartieDetailComponent,
	TruncateSeeMoreComponent,
	UnarchiveAffaireModalComponent
} from '@core-lib/components';
import { AppConstants } from '@core-lib/config';
import {
	DragModalDirective,
	IbanDirective,
	InputRestrictionDirective,
	InputUppercaseDirective,
	SirenDirective,
	SwiftDirective,
	QuillEditorDirective,
	AppCollapseDirective
} from '@core-lib/directives';
import { AuthGuard, RouteWithPermissionsGuard, RouteWithRolesGuard, UserInfoResolver } from '@core-lib/guards';
import { ErrorInterceptor } from '@core-lib/interceptors';
import {
	AuthNotificationService,
	CarpaDataTableColumnComponent,
	CarpaDataTableComponent,
	DocumentGedApiService,
	DocumentGedService,
	ModalRef,
	NetworkStatusComponent,
	NetworkStatusService,
	NotificationService,
	TokenExpiredModalComponent,
	UploadDocumentComponent,
	UploadDocumentModalComponent,
} from '@core-lib/modules';
import { AmountPipe, DateFormatPipe, HasPermissionPipe, HasRolePipe, PluralPipe, TruncatePipe } from '@core-lib/pipes';
import { BreadcrumbService, CookieService, RibService, UserService } from '@core-lib/services';
import { InitStoreActions, InitStoreSelectors, InitStoreState } from '@core-lib/store/init-store';
import { NgSelectModule } from '@ng-select/ng-select';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AppAlertComponent } from '@core-lib/components/app-alert/app-alert.component';
import { TabsetComponent } from './components/tabset/tabset.component';
import { TabComponent } from '@core-lib/components/tabset/tab/tab.component';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipModule } from '@angular/material/tooltip';
import { PaginationComponent } from '@core-lib/components/pagination/pagination.component';
import { PagerComponent } from '@core-lib/components/pager/pager.component';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { ToastrModule } from 'ngx-toastr';
import { filter } from 'rxjs/operators';
import { AdresseComponent } from './components/adresse/adresse.component';
import { CoordonneesBancairesComponent } from './components/coordonnees-bancaires/coordonnees-bancaires.component';
import { DataTableComponent } from './components/data-table/data-table.component';
import { DatePickerRangeComponent } from './components/date-picker-range/date-picker-range.component';
import { FraudeVirementAlerteModal } from './components/fraude-virement-alerte-modal/fraude-virement-alerte-modal.component';
import { MailModalComponent } from './components/mail-modal/mail-modal.component';
import { MontantPickerRangeComponent } from './components/montant-picker-range/montant-picker-range.component';
import { RibCompteProComponent } from './components/rib-compte-pro/rib-compte-pro.component';
import { BeneficiaireEffectifFormComponent } from './components/beneficiaire-effectif-form/beneficiaire-effectif-form.component';
import { CustomPhoneInputComponent } from './components/custom-phone-input/custom-phone-input.component';
import { TiersListComponent } from './components/tiers-list/tiers-list.component';
import { AppTooltipDirective } from './directives/app-tooltip.directive';
import { TelephoneDirective } from './directives/telephone.directive';
import { environmentCore } from './environments';
import { CarpaResolver } from './guards/carpa-resolver';
import { RouteWithParisTenantGuard } from './guards/route-with-paris-tenant.guard';
import {
	NewVersionUpdateNotificationComponent,
	VERSION_CHECK_INTERVAL_TIME,
	VersionCheckWorkerService
} from './modules/app-version-manger';
import { ScannerComponent } from './modules/scanner/components/scanner.component';
import { BreakLinePipe } from './pipes/break-line.pipe';
import { FilterPaysListPipe } from './pipes/filter-pays-liste.pipe';
import { TelephoneMaskPipe } from './pipes/telephone-mask.pipe';
import { CommonAlfrescoService } from './services/common-alfresco.service';
import { MailService } from './services/mail.service';
import { SettingsPageComponent } from './components/settings/settings-page.component';
import { SettingsContactComponent } from './components/settings/settings-contact/settings-contact.component';
import { SettingsNotificationsComponent } from './components/settings/settings-notifications/settings-notifications.component';
import { SettingReleveAffaireComponent } from './components/settings/settings-releve-affaire/settings-releve-affaire.component';
import { SettingChequeComponent } from './components/settings/settings-cheque/settings-cheque.component';
import { DateFormatDirective } from './directives/date-format.directive';
import { SettingsModeReceptionComponent } from './components/settings/settings-mode-reception/settings-mode-reception.component';
import { HistoryComponent } from './components/history/history.component';
import { HistoryModalComponent } from './components/history-modal/history-modal.component';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { MAT_DATE_LOCALE, MAT_DATE_FORMATS, MatNativeDateModule, DateAdapter } from '@angular/material/core';
import { FrenchDateAdapter } from '@core-lib/classes/date-adapter.class';

@NgModule({
	imports: [
		// Angular Core
		CommonModule,
		NgSelectModule,
		RouterModule,
		FormsModule,
		ReactiveFormsModule,
		TranslateModule,
		// Material
		MatTooltipModule,
		MatButtonModule,
		MatMenuModule,
		MatDatepickerModule,
		MatTimepickerModule,
		MatNativeDateModule,
		// Other vendors
		NgxMaskDirective,
		NgxMaskPipe,
		ToastrModule.forRoot({ closeButton: true, timeOut: 5000, tapToDismiss: false })
	],
	declarations: [
		PageHeaderComponent,
		AppBreadcrumbComponent,
		AppLogoComponent,
		LoaderSpinnerComponent,
		LoaderButtonSpinnerComponent,
		LabelHelpComponent,
		FormControlFeedbackComponent,
		PluralPipe,
		InputGroupAddDropdownComponent,
		AppAutocompleteComponent,
		TruncateSeeMoreComponent,
		DatagridTableComponent,
		DatagridPagingComponent,
		DatagridRowEmptyComponent,
		DatagridActionsDropdownComponent,
		ContentBlockComponent,
		BtnToggleComponent,
		ConfirmationModalComponent,
		ConfirmationEditionModalComponent,
		HasPermissionPipe,
		HasRolePipe,
		AmountPipe,
		DateFormatPipe,
		InputRestrictionDirective,
		InputUppercaseDirective,
		IbanDirective,
		TruncatePipe,
		DataTableComponent,
		UploadDocumentModalComponent,
		CoordonneesBancairesComponent,
		UploadDocumentComponent,
		DragModalDirective,
		BreakLinePipe,
		FilterPaysListPipe,
		CommentairesModalComponent,
		CommentairesHistoryComponent,
		CommentairesFormComponent,
		SirenDirective,
		TelephoneDirective,
		TiersFormComponent,
		TiersPartieDetailComponent,
		SwiftDirective,
		DataTableComponentV2,
		TiersListComponent,
		GenerationStatementModalComponent,
		AdresseComponent,
		NewVersionUpdateNotificationComponent,
		DatePickerRangeComponent,
		MontantPickerRangeComponent,
		MailModalComponent,
		UnarchiveAffaireModalComponent,
		ScannerComponent,
		DocumentBasicUploadComponent,
		DocumentUploadBaseComponent,
		AppTooltipDirective,
		TokenExpiredModalComponent,
		NetworkStatusComponent,
		CarpaDataTableComponent,
		CarpaDataTableColumnComponent,
		FraudeVirementAlerteModal,
		TelephoneMaskPipe,
		RibCompteProComponent,
		BeneficiaireEffectifFormComponent,
		SettingsPageComponent,
		SettingsContactComponent,
		SettingsNotificationsComponent,
		SettingReleveAffaireComponent,
		SettingChequeComponent,
		SettingsModeReceptionComponent,
		CustomPhoneInputComponent,
		DateFormatDirective,
		HistoryComponent,
		HistoryModalComponent,
		QuillEditorDirective,
		AppCollapseDirective,
		PagerComponent,
		PaginationComponent,
		TabsetComponent,
		TabComponent,
		AppAlertComponent
	],
	exports: [
		PageHeaderComponent,
		AppBreadcrumbComponent,
		AppLogoComponent,
		LoaderSpinnerComponent,
		LoaderButtonSpinnerComponent,
		LabelHelpComponent,
		FormControlFeedbackComponent,
		PluralPipe,
		InputGroupAddDropdownComponent,
		AppAutocompleteComponent,
		TruncateSeeMoreComponent,
		DatagridTableComponent,
		DatagridPagingComponent,
		DatagridRowEmptyComponent,
		DatagridActionsDropdownComponent,
		ContentBlockComponent,
		BtnToggleComponent,
		HasPermissionPipe,
		HasRolePipe,
		AmountPipe,
		DateFormatPipe,
		InputRestrictionDirective,
		InputUppercaseDirective,
		TruncatePipe,
		DataTableComponent,
		DragModalDirective,
		ToastrModule,
		CoordonneesBancairesComponent,
		BreakLinePipe,
		FilterPaysListPipe,
		DataTableComponentV2,
		TiersListComponent,
		GenerationStatementModalComponent,
		AdresseComponent,
		NewVersionUpdateNotificationComponent,
		UploadDocumentComponent,
		DatePickerRangeComponent,
		MontantPickerRangeComponent,
		UnarchiveAffaireModalComponent,
		DocumentBasicUploadComponent,
		DocumentUploadBaseComponent,
		AppTooltipDirective,
		TokenExpiredModalComponent,
		NetworkStatusComponent,
		CarpaDataTableComponent,
		CarpaDataTableColumnComponent,
		SwiftDirective,
		SirenDirective,
		TelephoneDirective,
		IbanDirective,
		TelephoneMaskPipe,
		RibCompteProComponent,
		CommentairesModalComponent,
		CommentairesFormComponent,
		CommentairesHistoryComponent,
		BeneficiaireEffectifFormComponent,
		SettingsPageComponent,
		SettingsContactComponent,
		SettingsNotificationsComponent,
		SettingReleveAffaireComponent,
		SettingChequeComponent,
		SettingsModeReceptionComponent,
		CustomPhoneInputComponent,
		DateFormatDirective,
		HistoryComponent,
		HistoryModalComponent,
		QuillEditorDirective,
		AppCollapseDirective,
		TabsetComponent,
		TabComponent,
		PagerComponent,
		PaginationComponent,
		AppAlertComponent
	],
	providers: [
		AuthGuard,
		RouteWithRolesGuard,
		RouteWithPermissionsGuard,
		RouteWithParisTenantGuard,
		CarpaResolver,
		UserInfoResolver,
		TranslatePipe,
		{
			provide: APP_INITIALIZER,
			useFactory: AppInitilizeFactory,
			deps: [Store],
			multi: true
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: ErrorInterceptor,
			deps: [TranslateService, Store],
			multi: true
		},
		{
			provide: APP_INITIALIZER,
			useFactory: BreadCrumbServiceInitializeFactory,
			deps: [BreadcrumbService],
			multi: true
		},
		{ provide: MAT_DATE_LOCALE, useValue: 'fr-FR' },
		{ provide: MAT_DATE_FORMATS, useValue: AppConstants.DATE_TIME_FORMATS },
  		{ provide: DateAdapter, useClass: FrenchDateAdapter },

		{ provide: ModalRef, useFactory: () => new ModalRef(() => { }) },

		{
			provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
			useValue: {
				// showDelay: 300,
				// hideDelay: 0,
				// touchendHideDelay: 0,
				tooltipClass: 'mat-tooltip'
			}
		},

		DocumentGedService,
		DocumentGedApiService,
		UserService,
		RibService,
		MailService,
		CommonAlfrescoService,
		NotificationService,
		AuthNotificationService,
		NetworkStatusService,
		VersionCheckWorkerService,
		CookieService,
		{ provide: VERSION_CHECK_INTERVAL_TIME, useValue: environmentCore.newVersionCheckIntervalMs },
		provideNgxMask()
	]
})
export class CoreModule { }

export function AppInitilizeFactory(store: Store<InitStoreState.IInitStoreState>) {
	return () =>
		new Promise(resolve => {
			store.dispatch(InitStoreActions.InitStart());
			store
				.select(InitStoreSelectors.selectIsInitFinished)
				.pipe(filter(ready => ready))
				.subscribe(isFinished => {
					resolve(isFinished);
				});
		});
}

export function BreadCrumbServiceInitializeFactory(breadcrumbService: BreadcrumbService) {
	return () => breadcrumbService.init();
}
